import {AgoraVideoPlayer, createMicrophoneAndCameraTracks} from "agora-rtc-react";
import AgoraRTC from 'agora-rtc-sdk-ng';
import VirtualBackgroundExtension from "agora-extension-virtual-background";
import {useEffect, useRef, useState, useCallback} from "react";
import Videos from "./Video";
import {createRemoteLeaveHandler} from "../../../utils/inAppCallEnd";
import Controls from "./Controls";
import styles from "./AgoraComponent.module.css";
import Spinner from "../../../components/Spinner";
import classes from "./AgoraComponent.module.css";
import microphone from "../../../assets/microphone.png";
import muteMicrophone from "../../../assets/mute-microphone.png";
const muteCamera = require('../../../assets/mute-camera.png');

const useMicrophoneAndCameraTracks = createMicrophoneAndCameraTracks();
export const VideoCall = (props) => {
	const {setInCall, channelName, client, appId, token, onRemoteHangup} = props;
	const [users, setUsers] = useState([]);
	const [start, setStart] = useState(false);
	const {ready, tracks} = useMicrophoneAndCameraTracks();
	const [remoteConnectionState, setRemoteConnectionState] = useState({audio: true, video: true});
	const remoteHangupHandledRef = useRef(false);
	const hasJoinedRef = useRef(false);
	// A patient whose app went silent (Agora "ServerTimeOut") gets RECONNECT_GRACE_MS to come back
	// before the call ends; a hang-up still ends it at once.
	const [reconnecting, setReconnecting] = useState(false);
	const finalizeRef = useRef(null);
	const leaveHandlerRef = useRef(null);
	if (!leaveHandlerRef.current) {
		leaveHandlerRef.current = createRemoteLeaveHandler({
			endCall: () => finalizeRef.current?.(),
			onReconnecting: setReconnecting,
		});
	}

	const teardownLocalTracks = useCallback(() => {
		if (!Array.isArray(tracks)) return;
		tracks.forEach((track) => {
			try { track?.stop?.(); } catch (e) {}
			try { track?.close?.(); } catch (e) {}
		});
	}, [tracks]);

	const finalizeCallAfterRemoteLeft = useCallback(() => {
		if (remoteHangupHandledRef.current) return;
		remoteHangupHandledRef.current = true;
		setUsers([]);
		setStart(false);
		teardownLocalTracks();
		if (typeof onRemoteHangup === "function") {
			onRemoteHangup();
			return;
		}
		if (typeof setInCall === "function") {
			setInCall(false);
		}
	}, [onRemoteHangup, setInCall, teardownLocalTracks]);
	finalizeRef.current = finalizeCallAfterRemoteLeft;

	const init = async (name) => {
		client.on("user-joined", () => leaveHandlerRef.current.userBack());
		client.on("user-published", async (user, mediaType) => {
			leaveHandlerRef.current.userBack();
			remoteHangupHandledRef.current = false;
			await client.subscribe(user, mediaType);
			setUsers((prevUsers) => {
				const filtered = prevUsers.filter((u) => u.uid !== user.uid);
				return [...filtered, user];
			});

			if (mediaType === "audio") {
				user.audioTrack?.play();
				if (props.onAudioStreamsReady && tracks?.[0]) {
					props.onAudioStreamsReady(tracks[0], user.audioTrack);
				}
				setRemoteConnectionState((prev) => ({ ...prev, audio: true }));
			} else if (mediaType === "video") {
				setRemoteConnectionState((prev) => ({ ...prev, video: true }));
			}
		});

		client.on("user-unpublished", (user, type) => {
			if (type === "audio") user.audioTrack?.stop();
			if (type === "video") user.videoTrack?.stop?.();
			setRemoteConnectionState((prev) => ({ ...prev, [type]: false }));
			setUsers((prevUsers) => prevUsers.filter((u) => u.uid !== user.uid));
		});

		client.on("user-left", (user, reason) => {
			user.audioTrack?.stop?.();
			user.videoTrack?.stop?.();
			setRemoteConnectionState({audio: false, video: false});
			setUsers((prevUsers) => prevUsers.filter((u) => u.uid !== user.uid));
			leaveHandlerRef.current.userLeft(reason);
		});

		hasJoinedRef.current = true;
		await client.join(appId, name, token, props.account);
		if (tracks) await client.publish([tracks[0], tracks[1]]);
		setStart(true);
	};

	useEffect(() => {
		if (!ready || !tracks || start || hasJoinedRef.current) return;
		init(channelName).catch((err) => console.log("video init", err));
	}, [channelName, ready, tracks, start]);

	useEffect(() => () => {
		try { client.removeAllListeners?.(); } catch (e) {}
		hasJoinedRef.current = false;
		leaveHandlerRef.current?.dispose();
	}, [client]);

	return (
		<>
			{reconnecting && (
				<div style={{background: "#fff4e5", color: "#8a5300", padding: "6px 12px", borderRadius: 6, margin: "6px 0", fontSize: 13}}>
					Patient reconnecting… the call ends if they don't come back within 45 seconds.
				</div>
			)}
			{ready && tracks && (
				<div>
					<div>
						{start && users.length > 0 ? (
							users.map((user) =>
								user.videoTrack && remoteConnectionState.video ? (
									<div key={user.uid}>
										{!remoteConnectionState.audio && (
											<div className={styles.muteAudioIconRemote}>
												<img className={classes.icon_image_small} src={muteMicrophone} alt="" />
											</div>
										)}
										<AgoraVideoPlayer className={`vid ${styles.video_frame_remote}`} videoTrack={user.videoTrack} />
									</div>
								) : (
									<div key={user.uid} className={`vid ${styles.video_frame_black_remote}`}>
										<img className={classes.icon_image} src={muteCamera} alt="" />
										<img className={classes.icon_image} src={remoteConnectionState.audio ? microphone : muteMicrophone} alt="" />
									</div>
								)
							)
						) : (
							<div className={`vid ${styles.video_frame_black_remote}`}>
								<Spinner color="white" isLoading />
							</div>
						)}

						{start && tracks ? (
							<AgoraVideoPlayer className={`vid ${styles.video_frame}`} videoTrack={tracks[1]} />
						) : (
							<div className={`vid ${styles.video_frame_black}`}>
								<Spinner color="white" isLoading />
							</div>
						)}
					</div>

					<div>
						<Controls tracks={tracks} setStart={setStart} setInCall={setInCall} client={client} type="videoCall" />
					</div>
				</div>
			)}
		</>
	);
};
